import { useCallback, useEffect, useState } from 'react';
import { useAppDispatch } from '../../../common/hooks/useAppDispatch';
import { useAppSelector } from '../../../common/hooks/useAppSelector';
import { selectUser } from '../../../slices/user/userSlice';
import { DOCUMENT_CATEGORIES } from '../types';
import { LibraryItem } from '../components/library-list/types';
import { selectAllCompanies } from '../../../slices/companies/companiesSlice';
import { NWClient } from '../../../client/NWClient';
import {
  clearDocumentTemplates,
  fetchDocumentTemplates,
} from '../../../slices/document-templates/documentTemplatesSlice';
import { fetchDisclaimerInfo } from '../../../slices/disclaimer/disclaimerSlice';
import { ROUTES } from '../../../common/constants/routes';
import { useLocation, useSearchParams } from 'react-router-dom';
import { fetchEmployees } from '../../../slices/employees/employeesSlice';
import { fetchCustomers } from '../../../slices/customers/customersSlice';
import { fetchSuppliers } from '../../../slices/suppliers/suppliersSlice';
import { fetchBusinessProfileContactById } from '../../../slices/business-profile/businessProfileSlice';
import { RootState } from '../../../store';
import { useCheckPermission } from '../../../common/hooks/useCheckPermission';

type SearchParams = {
  category?: DOCUMENT_CATEGORIES;
  region?: string;
  name?: string;
};

export const useLibrary = () => {
  const currentUser = useAppSelector(selectUser);
  const company = useAppSelector(selectAllCompanies)[0];
  const checkPermission = useCheckPermission();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const employeesLoading = useAppSelector((state: RootState) => state.employees.status);
  const customersLoading = useAppSelector((state: RootState) => state.customers.status);
  const suppliersLoading = useAppSelector((state: RootState) => state.suppliers.status);
  const businessDetailsLoading = useAppSelector((state: RootState) => state.businessProfile.status);
  const [documentLibraryItems, setDocumentLibraryItems] = useState<LibraryItem[]>([]);
  const [cardView, setCardView] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<DOCUMENT_CATEGORIES>('All');
  const [libraryType, setLibraryType] = useState<null | 'businessKitz' | 'own'>('businessKitz');
  const [workflow, setWorkflow] = useState<string | null>(null);
  const [pendingStatus, setPendingStatus] = useState<boolean>(false);
  const [region, setRegion] = useState<'uk' | 'au' | 'global'>('au');
  const [disclaimerMessage, setDisclaimerMessage] = useState<boolean>(false);
  const [employmentDataMessage, setEmploymentDataMessage] = useState<boolean>(false);
  const [searchString, setSearchString] = useState('');
  const [searchStringInputValue, setSearchStringInputValue] = useState('');
  const [searchCleared, setSearchCleared] = useState(false);
  const libraryItems = [...documentLibraryItems];
  const [params] = useSearchParams();
  const isBusinessKitzLibrary = params.get('businessKitzLibrary');
  const isOwnLibrary = params.get('ownLibrary');
  const handleTabChange = (tab: DOCUMENT_CATEGORIES) => {
    setActiveTab(tab);
    setWorkflow(null);
  };

  const getSearchParams = useCallback(
    (basicParams: SearchParams) => {
      return searchString ? { ...basicParams, name: searchString } : basicParams;
    },
    [searchString]
  );

  const retrieveTemplates = useCallback(() => {
    if (libraryType === 'businessKitz') {
      const basicParams = activeTab === 'All' ? { region } : { category: activeTab, region };
      NWClient.list('document-library', getSearchParams(basicParams))
        .then((res: LibraryItem[]) => {
          const templatesSortedByName = [...res].sort((a, b) => a.name.localeCompare(b.name));
          setDocumentLibraryItems(templatesSortedByName);
        })
        .finally(() => {
          setPendingStatus(false);
        });
    } else {
      const basicParams = activeTab === 'All' ? {} : { category: activeTab };
      dispatch(
        fetchDocumentTemplates({
          data: { ...getSearchParams(basicParams), user: currentUser?.id },
        })
      )
        .unwrap()
        .finally(() => {
          setPendingStatus(false);
        });
    }
  }, [libraryType, activeTab, region, getSearchParams, dispatch, currentUser?.id]);

  const resetSearchFilter = useCallback(() => {
    setSearchString('');
    setSearchStringInputValue('');
    setSearchCleared(true);
    setPendingStatus(true);
    //retrieveTemplates();
  }, []);

  const setSearchStringValue = useCallback(() => {
    if (suppliersLoading !== 'loading') {
      setSearchString(searchStringInputValue);
    }
  }, [suppliersLoading, setSearchString, searchStringInputValue]);

  /*const getDocuments = useCallback(() => {
      if (currentUser.id) {
        dispatch(
          fetchDocuments({
            data: {
              user: currentUser?.id,
            },
          })
        );
      }
    }, [dispatch, currentUser?.id]);*/

  const setDefaultLibraryType = useCallback(() => {
    if (location.pathname === ROUTES.DOCUMENT_LIBRARY) {
      setLibraryType('businessKitz');
    }
  }, [location.pathname]);

  const handleRetrieveTemplates = useCallback(() => {
    setPendingStatus(true);
    setDocumentLibraryItems([]);
    dispatch(clearDocumentTemplates());
    retrieveTemplates();
  }, [dispatch, retrieveTemplates]);

  const getDisclaimerInfo = useCallback(() => {
    dispatch(fetchDisclaimerInfo());
  }, [dispatch]);

  const handleSearch = useCallback(() => {
    if (searchString && searchString.trim().length >= 3) {
      setPendingStatus(true);
      setCardView(false);
      retrieveTemplates();
    }
  }, [searchString, retrieveTemplates]);

  /*useEffect(() => {
      getDocuments();
    }, [getDocuments]);*/

  useEffect(() => {
    console.log(libraryType);
  }, [libraryType]);

  useEffect(() => {
    getDisclaimerInfo();
  }, [getDisclaimerInfo]);

  useEffect(() => {
    setDefaultLibraryType();
  }, [setDefaultLibraryType]);

  useEffect(() => {
    handleRetrieveTemplates();
  }, [handleRetrieveTemplates]);

  useEffect(() => {
    handleSearch();
  }, [handleSearch]);

  useEffect(() => {
    if (employeesLoading === 'idle') {
      dispatch(fetchEmployees({ company_id: company.id }));
    }
  }, [company, dispatch, employeesLoading]);

  useEffect(() => {
    if (customersLoading === 'idle') {
      dispatch(fetchCustomers({ company_id: company.id }));
    }
  }, [company, dispatch, customersLoading]);

  useEffect(() => {
    if (suppliersLoading === 'idle') {
      dispatch(fetchSuppliers({ company_id: company.id, page_size: 100 }));
    }
  }, [company, dispatch, suppliersLoading]);

  useEffect(() => {
    if (businessDetailsLoading === 'idle') {
      dispatch(fetchBusinessProfileContactById({ id: company.id }));
    }
  }, [company, dispatch, businessDetailsLoading]);

  useEffect(() => {
    if (isBusinessKitzLibrary) {
      setLibraryType('businessKitz');
    }
    if (isOwnLibrary) {
      setLibraryType('own');
    }
  }, [isBusinessKitzLibrary, isOwnLibrary]);

  useEffect(() => {
    return () => {
      resetSearchFilter();
    };
  }, [resetSearchFilter]);

  useEffect(() => {
    setSearchStringValue();
  }, [setSearchStringValue]);

  return {
    libraryItems,
    activeTab,
    handleTabChange,
    libraryType,
    setLibraryType,
    pendingStatus,
    region,
    setRegion,
    disclaimerMessage,
    setDisclaimerMessage,
    cardView,
    setCardView,
    workflow,
    setWorkflow,
    searchString,
    setSearchString,
    resetSearchFilter,
    searchCleared,
    setSearchCleared,
    employmentDataMessage,
    setEmploymentDataMessage,
    searchStringInputValue,
    setSearchStringInputValue,
  };
};
